import { Button, Divider, Modal, Select } from "antd";
import { useInfoUser } from "../../../../store/UserStore";
import { useCallingContext } from "../../../../context/CallingContext";
import { useEffect, useRef, useState } from "react";
import { RxDotFilled } from "react-icons/rx";

const MediaSetting = ({ open, onClose }) => {
  const {
    audioDevices,
    videoDevices,
    selectedAudioId,
    selectedVideoId,
    setSelectedAudioId,
    setSelectedVideoId,
    stopStream,
    replaceStream,
    setLocalStream,
    localStreamRef,
  } = useCallingContext();

  const { user, languageMap } = useInfoUser();
  const [videoReview, setVideoReview] = useState({
    deviceId: null,
    stream: null,
  });

  const [audioReview, setAudioReview] = useState({
    deviceId: null,
    stream: null,
  });

  const [soundLevel, setSoundLevel] = useState(0);
  const rafRef = useRef(null);

  const videoRef = useRef(null);
  const audioRef = useRef(null);

  const fetchAudioReview = async (deviceId) => {
    if (!deviceId || !open) return;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: deviceId } },
    });

    if (!audioRef?.current) {
      stopStream(stream);
      return;
    }

    audioRef.current.srcObject = stream;

    stopStream(audioReview?.stream);
    setAudioReview({
      deviceId,
      stream: stream,
    });
  };

  const fetchVideoReview = async (deviceId) => {
    if (!deviceId || !open) return;

    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: deviceId } },
    });

    if (!videoRef?.current) {
      stopStream(stream);
      return;
    }

    videoRef.current.srcObject = stream;

    stopStream(videoReview?.stream);
    setVideoReview({
      deviceId,
      stream: stream,
    });
  };

  const onConfirm = async () => {
    const videoTracks = videoReview?.stream?.getVideoTracks() ?? [];
    const audioTracks = audioReview?.stream?.getAudioTracks() ?? [];

    const newStream = new MediaStream([...videoTracks, ...audioTracks]);

    setSelectedVideoId(videoReview?.deviceId ?? null);
    setSelectedAudioId(audioReview?.deviceId ?? null);

    localStreamRef.current = newStream;

    setLocalStream((prev) => ({
      ...prev,
      stream: newStream,
    }));

    await replaceStream(videoReview?.stream, "video");
    await replaceStream(audioReview?.stream, "audio");

    onClose();
  };

  useEffect(() => {
    fetchVideoReview(selectedVideoId);
  }, [selectedVideoId]);

  useEffect(() => {
    fetchAudioReview(selectedAudioId);
  }, [selectedAudioId]);

  useEffect(() => {
    if (!audioReview?.stream) return;

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(audioReview?.stream);

    analyser.fftSize = 256;
    source.connect(analyser);

    const data = new Uint8Array(analyser.frequencyBinCount);

    const update = () => {
      analyser.getByteFrequencyData(data);

      const average = Math.min(
        Math.round(data.reduce((sum, value) => sum + value, 0) / data.length),
        20,
      );

      setSoundLevel(average);
      rafRef.current = requestAnimationFrame(update);
    };

    update();

    return () => {
      cancelAnimationFrame(rafRef.current);
      source.disconnect();
      audioContext.close();
    };
  }, [audioReview?.stream]);

  return (
    <Modal
      title={languageMap?.["sle"] ?? "Device setting"}
      width="500px"
      // centered
      open={open}
      onCancel={onClose}
      footer={false}
    >
      <div className="bg-[#eafbf3] p-[5px] text-[16px] border-[1px] border-[green]">
        Camera is working
      </div>
      <div className="p-[5px] text-[14px] mt-[5px]">Camera</div>
      <Select
        className="w-[100%]"
        value={videoReview?.deviceId}
        options={videoDevices}
        onChange={fetchVideoReview}
      />
      <div className="mt-[10px] w-full h-[260px] bg-black rounded overflow-hidden">
        <video ref={videoRef} autoPlay playsInline muted />
      </div>
      <Divider />
      <div className="bg-[#eafbf3] p-[5px] text-[16px] border-[1px] border-[green]">
        Micro is working
      </div>
      <div className="p-[5px] text-[14px] mt-[5px]">Micro</div>
      <Select
        className="w-[100%]"
        value={selectedAudioId}
        options={audioDevices}
        onChange={fetchAudioReview}
      />
      <audio ref={audioRef} autoPlay playsInline />
      <div className="mt-[10px] flex">
        {soundLevel > 0 &&
          Array.from({ length: soundLevel }).map((_, i) => (
            <RxDotFilled key={i} />
          ))}
      </div>
      <div className="flex justify-end gap-2 mt-[30px]">
        <Button
          className="!bg-[#ffffff] !text-black hover:!bg-[#d9d9d9] font-medium w-[90px]"
          onClick={onClose}
        >
          {languageMap?.["modal.cancel"] ?? "Cancel"}
        </Button>
        <Button
          className="!bg-[#ff4d4f] !text-white hover:!bg-[#ff7875] font-medium w-[90px]"
          onClick={onConfirm}
        >
          {languageMap?.["modal.ok"] ?? "OK"}
        </Button>
      </div>
    </Modal>
  );
};

export { MediaSetting };
